import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { RequestHistoryComponent } from '@components/request-history/request-history.component';
import {
  ActionsPanelComponent,
  FloatingActionsData,
  ModalActionEnum
} from '@dialogs/actions-panel/actions-panel.component';
import { ConfirmationDialogComponent } from '@dialogs/confirmation-dialog/confirmation-dialog.component';
import { InlineFormSchemaDialogComponent } from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import {
  ColorAliases,
  GridActions,
  GridOverrides,
  HideFieldOperator,
  ImportPFCBomResponse,
  NewUiRoute,
  parseAsUTC,
  PcfQuestionnaireResult,
  QuestionnaireSchema,
  RowSelection
} from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  ButtonComponent,
  ButtonToggleComponent,
  FormDataService,
  getFieldType,
  GridActionsComponent,
  GridModule,
  GridNoRowsComponent,
  HeaderComposeComponent,
  LoaderModule,
  NumbersWidgetComponent
} from '@mds/ui';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { ButtonTypes } from '@pages/dashboards/scope-3/utils/constants';
import { CampaignTabsEnum } from '@pages/data-collection/helpers/data-collection-tabs';
import { InformationRequestService } from '@pages/information-requests/information-request.service';
import { LocalStorageService } from '@services/local-storage.service';
import { ComposerApiService } from '@services/composer-api.service';
import { FileUploadService } from '@services/file-upload.service';
import { MenuService } from '@services/menu.service';
import { SupplierRequestApiService } from '@services/supplier-request-api.service';
import { QuestionnairesValidatorService } from '@services/questionnaires-validator.service';
import { ToastrService } from '@services/toaster.service';
import { sortBy } from '@utils/sort-by';
import { ColDef, GridApi, RowNode } from 'ag-grid-enterprise';
import _ from 'lodash';
import { NgxMasonryModule } from 'ngx-masonry';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { forkJoin, throwError } from 'rxjs';
import { catchError, debounceTime, filter, finalize, startWith, switchMap, tap } from 'rxjs/operators';
import { resultsData } from './pcf-questionnaire.model';

@Component({
  standalone: true,
  imports: [
    MdsIconsModule,
    FormsModule,
    NgxMasonryModule,
    ReactiveFormsModule,
    FormlyModule,
    MdsIconsModule,
    GridModule,
    LoaderModule,
    ButtonComponent,
    PerfectScrollbarModule,
    MatDialogModule,
    HeaderComposeComponent,
    NumbersWidgetComponent,
    ActionsPanelComponent,
    ButtonToggleComponent,
    RequestHistoryComponent
  ],
  selector: 'pcf-questionnaire',
  templateUrl: './pcf-questionnaire.component.html',
  styleUrls: ['./pcf-questionnaire.component.scss'],
  providers: [DatePipe, QuestionnairesValidatorService, InformationRequestService, NewUiRoute]
})
export class PcfQuestionnaireComponent implements OnInit, OnChanges {
  icons = MdsIcon;
  @Input() productInfo: any;
  @Input() results: any;
  @Input() reporting: any;
  @Input() materialBreakdown: any;
  @Input() rid: string;
  @Input() title = '';
  @Output() reload = new EventEmitter<void>();
  productInfoFieldTypes: FieldType[] = [];
  productInfoForm = new FormGroup({});
  reportingFieldTypes: FieldType[] = [];
  reportingForm = new FormGroup({});
  productFootprintResults: PcfQuestionnaireResult;
  productFootPrintUnits: Record<string, any> = {};
  gridApi: GridApi;
  calculateCarbonFootprint: boolean;
  isReporting: boolean;
  carbonValuePlaceholder: string;
  isCalculationMethod: boolean;
  isBomImportInprogress: boolean;
  saveAction: FloatingActionsData = {
    title: '',
    actions: [
      {
        label: 'Save',
        icon: MdsIcon.SAVE,
        value: ModalActionEnum.SAVE,
        callback: () => this.saveRequestData(false)
      }
    ]
  };
  resultSection = resultsData;

  successColor = ColorAliases.SUCCESS_CONTAINER;
  errorContainer = ColorAliases.ERROR_CONTAINER;

  materialsGridOverrides: GridOverrides;
  processesGridOverrides: GridOverrides;
  materialsGridApi: GridApi;
  processesGridApi: GridApi;
  tabs = [
    { label: 'Questionnaire', value: CampaignTabsEnum.QUESTIONNAIRES, icon: MdsIcon.CAMPAIGNS },
    { label: 'History', value: CampaignTabsEnum.HISTORY, icon: MdsIcon.HISTORY }
  ];
  selectedTab: CampaignTabsEnum = CampaignTabsEnum.QUESTIONNAIRES;
  campaignTabs = CampaignTabsEnum;

  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private informationRequestService = inject(InformationRequestService);
  private toastr = inject(ToastrService);
  private dialog = inject(MatDialog);
  private fileUploadService = inject(FileUploadService);
  private composerApiService = inject(ComposerApiService);
  private supplierRequestApiService = inject(SupplierRequestApiService);
  private cdr = inject(ChangeDetectorRef);
  private localStorageService = inject(LocalStorageService);
  private formDataService = inject(FormDataService);
  private routePipe = inject(NewUiRoute);

  isUserExternalSupplier = this.menuService.isExternalSupplier();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.reporting.firstChange && changes.reporting.currentValue) {
      this.init();
    }
  }

  ngOnInit(): void {
    this.init();
    this.onMenuActions();
    this.showAttentionInfoDialog();
  }

  onMenuActions() {
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(300)).subscribe((value) => {
      if (!value) return;
      if (value.key === 'submit') {
        this.saveRequestData(true);
        this.menuService.clearSelectedAction();
      }
    });
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value) {
        this.init();
      }
    });
  }

  /**
   * Clears stored results for specified forms from local storage.
   */
  clearStoredResults() {
    const forms = ['productInfoForm', 'reportingForm'];

    forms.forEach((form) => {
      const key = this.generateStorageKey(form);
      this.localStorageService.removeItem(key);
    });
  }

  submitRequestData() {
    this.supplierRequestApiService.submitRequestData(this.rid, 'submitted').subscribe({
      next: () => {
        this.toastr.showMessage('Request data submitted successfully', 2000);
        this.menuService.setSelectedAction(null);
        this.reload.emit();
      },
      error: () => {}
    });
  }

  /**
   * Handles saving or submitting the form data, with validations, file uploads, and API requests.
   * If the product calculation method is "PCF", ensures all required fields are valid before proceeding.
   *
   * @param isSubmit - A boolean indicating whether to submit the data (true) or just save it (false).
   */
  saveRequestData(isSubmit?: boolean): void {
    const reportingFormValues: any = this.reportingForm.getRawValue();
    const productInfoFormValues: Record<string, any> = this.productInfoForm.getRawValue();
    if (productInfoFormValues.calculation_method === 'PCF' && !this.reportingForm.valid) {
      this.formDataService.markControlsTouched(this.reportingForm);
      this.toastr.showMessage('Please make sure all required fields are filled out', 4000);
      return;
    }
    this.clearStoredResults();
    const files = reportingFormValues?.out_upload_files ?? [];
    (Array.isArray(files) && files?.length ? this.uploadFileAndHandleResponse(files[0]) : this.makeApiRequests())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([productInfo]) => {
          if (isSubmit) {
            this.submitRequestData();
          } else {
            this.toastr.showMessage('Answers successfully saved', 3000);
            this.reload.emit();
          }
        },
        error: (error) => this.showError(error)
      });
  }

  showAttentionInfoDialog() {
    if (this.reporting && this.reporting.actions.edit) {
      this.dialog.open(ConfirmationDialogComponent, {
        data: {
          header: 'Attention',
          text: 'If you have a calculated product carbon footprint (PCF) available, please submit this information. Otherwise, please provide details on the material breakdown of your product. ',
          type: ButtonTypes.INFO,
          confirm_text: 'OK'
        }
      });
    }
  }

  /**
   * Handles grid action events based on the event type.
   * @param params - The parameters containing the event type and the grid node.
   * @param isMaterials
   */
  gridActionsEvent(params: { event: string; node: RowNode }, isMaterials: boolean): void {
    const { event, node } = params;
    if (event === GridActions.EDIT) {
      this.addRow(isMaterials, node);
    } else if (event === GridActions.VIEW) {
      const url = node.data?.Link_Inputs?.url;
      if (url) {
        window.open(this.routePipe.transform(url, true), '_blank');
      }
    } else if (event === GridActions.DELETE) {
      this.removeSelected(isMaterials, node.data);
    }
  }

  getGridApi(api: GridApi) {
    this.gridApi = api;
  }

  getMaterialsGridApi(api: GridApi) {
    this.materialsGridApi = api;
  }
  getProcessesGridApi(api: GridApi) {
    this.processesGridApi = api;
  }

  addRow(isMaterials: boolean, row?: RowNode) {
    const existingLines = row?.data.row || [];

    // Select schema based on material/process type
    const formSchema = (
      isMaterials ? this.materialBreakdown.materials.formSchema : this.materialBreakdown.processes.formSchema
    ).map((schema: any) => this.configureSchema(schema));

    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: { formSchema, lines: existingLines }
    });

    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => this.handleSubmitData(data, existingLines, dialogRef, isMaterials));
  }

  /** Configure schema behavior dynamically based on field key */
  private configureSchema(schema: any): any {
    switch (schema.key) {
      case 'out_u_l_impact.pcf_amount':
        // Hide regardless of selection – needed for PCF creation
        schema.hide_options_key = 'in_u_intermediate.in';
        schema.hide_options_value = 'in_u_intermediate.out';
        break;

      case 'in_u_intermediate.out':
        schema.hideFieldByCondition = {
          fieldKey: 'unit',
          condition: {
            fieldProperty: 'enum',
            operator: HideFieldOperator.NotIncludes,
            tileProperty: 'unit'
          }
        };
        break;
      default:
        break;
    }

    return schema;
  }

  /** Handles data emitted from dialog submit */
  private handleSubmitData(data: any, existingLines: any[], dialogRef: any, isMaterials: boolean): void {
    if (!data?.value?.Inputs) return;

    const updateValues = data.updatedValues || data.value.Inputs;
    const payload = this.buildPayload(existingLines, updateValues);
    payload.supplier_request = this.rid;

    this.saveGridLine(payload, dialogRef, isMaterials);
  }

  /** Build payload from both existing lines and updated form values */
  private buildPayload(lines: any[], updateValues: any): any {
    const payload: any = {};
    const allowedKeys = [
      'in_u_intermediate.amount',
      'in_u_intermediate.out',
      'in_u_intermediate.rid',
      'in_u_intermediate.unit',
      'out_u_l_impact.out',
      'out_u_l_impact.pcf_amount'
    ];

    // Extract existing values
    for (const line of lines) {
      if (!allowedKeys.includes(line.key) || line.value == null) continue;

      const [section, field] = line.key.split('.');
      payload[section] = payload[section] || {};
      payload[section][field] = line.value;
    }

    // Ensure out_u_l_impact.out mirrors in_u_intermediate.out when PCF is present
    if (payload?.out_u_l_impact?.pcf_amount != null) {
      payload.out_u_l_impact.out = payload.in_u_intermediate?.out;
    }

    // Merge updated values
    this.mergeIntermediateValues(payload, updateValues);
    this.mergeImpactValues(payload, updateValues);

    return payload;
  }

  /** Merge updates for in_u_intermediate */
  private mergeIntermediateValues(payload: any, updateValues: any): void {
    const input = updateValues?.in_u_intermediate;
    if (!input || Object.keys(input).length === 0) return;

    payload.in_u_intermediate = payload.in_u_intermediate || {};
    if (input.out) payload.in_u_intermediate.out = input.out;
    if (input.amount) payload.in_u_intermediate.amount = input.amount;
    if (updateValues.unit) payload.in_u_intermediate.unit = updateValues.unit;
  }

  /** Merge updates for out_u_l_impact */
  private mergeImpactValues(payload: any, updateValues: any): void {
    const output = updateValues?.out_u_l_impact;
    if (!output || Object.keys(output).length === 0) return;

    payload.out_u_l_impact = payload.out_u_l_impact || {};
    if (updateValues.in_u_intermediate?.out) {
      payload.out_u_l_impact.out = updateValues.in_u_intermediate.out;
    }
    if (output.pcf_amount != null) {
      payload.out_u_l_impact.pcf_amount = output.pcf_amount;
    }
  }

  saveGridLine(payload, dialogRef: MatDialogRef<InlineFormSchemaDialogComponent>, isMaterials: boolean): void {
    const target = this.materialBreakdown.target.split('/');
    const targetRid = target[target.length - 1];
    this.composerApiService
      .submitEditedMetadata(targetRid, payload, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: any) => {
          if (res.updated) {
            this.supplierRequestApiService.getSupplierRequest(this.rid, true).subscribe((res: any) => {
              if (isMaterials) {
                const materialsLines = res.material_breakdown.materials.lines;
                this.materialBreakdown.materials.lines = materialsLines;
                this.materialsGridOverrides.rowData = this.getRawData(materialsLines, true);
              } else {
                const processLines = res.material_breakdown.processes.lines;
                this.materialBreakdown.processes.lines = processLines;
                this.processesGridOverrides.rowData = this.getRawData(processLines, false);
              }
            });

            this.calculateCarbonFootprint = true;
            this.setResultsData();
            dialogRef?.close();
            this.cdr.markForCheck();
          } else if (res?.fails?.length || !res.is_success) {
            this.toastr.showMessage((res?.fails || []).map((f) => f?.message) ?? 'Something went wrong');
          }
        },
        (error) => {
          this.showError(error);
        }
      );
  }

  removeSelected(isMaterials: boolean, node?: RowNode) {
    const gridApi = isMaterials ? this.materialsGridApi : this.processesGridApi;
    let selectedRows = [];
    if (node) {
      selectedRows.push(node);
    } else if (gridApi) {
      selectedRows = gridApi.getSelectedRows();
    }
    const deletedRids = selectedRows.map((rows) => rows.row.find((r) => r.key === 'in_u_intermediate.rid').rid);
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: `Delete${deletedRids.length > 1 ? ' Items?' : ' Item?'}`,
        text: `${
          deletedRids.length + (deletedRids.length > 1 ? ' Items' : ' Item')
        } will be deleted. You can still restore the data from the Trash.`,
        cancel_text: 'Cancel',
        delete_text: 'Delete'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((confirm) => confirm), // Only continue if confirm is true
        switchMap(() => this.composerApiService.deleteMultipleDataset(deletedRids))
      )
      .subscribe(
        (res: any) => {
          if (res?.fails?.length) {
            this.toastr.showMessage((res?.fails || []).map((f) => f.message) ?? 'Something went wrong');
          } else {
            this.toastr.showMessage('Deleted Successfully', 1000);
            gridApi.applyTransaction({ remove: selectedRows });
            this.calculateCarbonFootprint = true;
            this.setResultsData();
          }
        },
        (error) => {
          this.showError(error);
        }
      );
  }

  recalculateImpact() {
    this.informationRequestService
      .calculateEnvironmentalImpacts(this.rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res: { amount: number; unit: string; impact_rid: string }) => {
          this.updateProductFootprintResults(res.amount);
          this.calculateCarbonFootprint = false;
          this.setResultsData();
          this.cdr.markForCheck();
        },
        (error) => {
          this.showError(error);
        }
      );
  }

  getColumnDefinition(schema: QuestionnaireSchema[], isMaterials: boolean): ColDef[] {
    const header: ColDef[] = [];

    header.push(
      {
        headerName: 'Action',
        field: 'actions',
        lockPosition: true,
        pinned: 'right',
        floatingFilter: false,
        filter: false,
        flex: 1,
        cellRenderer: GridActionsComponent,
        cellRendererParams: {
          label: 'Notify',
          onClick: (params: { event: string; node: RowNode }) => {
            this.gridActionsEvent(params, isMaterials);
          }
        }
      },
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        headerName: '',
        field: 'select',
        suppressMovable: true,
        lockPosition: true,
        minWidth: 35,
        width: 35,
        sortable: false,
        floatingFilter: false,
        filter: false,
        suppressHeaderMenuButton: true,
        suppressColumnsToolPanel: true
      }
    );
    sortBy(schema, 'sortlevel');

    schema.forEach((row) => {
      const key = row.key.split('.').pop() as string;
      const colObj: ColDef = {
        field: key,
        headerName: row.label || row.title,
        flex: 2,
        floatingFilter: true,
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => `${valueA}`.localeCompare(`${valueB}`)
      };

      // Apply specific configurations based on the key
      switch (key) {
        case 'out':
          colObj.editable = false;
          colObj.cellEditor = 'TileAutocompleteCellEditorComponent'; // TODO
          break;

        case 'amount':
          colObj.editable = false;
          colObj.cellEditor = 'agTextCellEditor';
          colObj.valueFormatter = (params: { value: unknown }) =>
            params.value != null ? Number(params.value).toFixed(2) : 'unknown';
          colObj.valueParser = (params: { newValue: unknown }) => (params.newValue ? Number(params.newValue) : '');
          break;

        default:
          break;
      }

      header.push(colObj);
    });

    return header;
  }

  /**
   *
   * @param locale - undefined to use user's local
   * @returns  Outputs "-.--" or "-,--" depending on the user's locale
   */
  getCarbonValuePlaceholder(locale = undefined) {
    const formattedNumber = new Intl.NumberFormat(locale).format(1.1);
    return `-${formattedNumber[1]}--`;
  }

  importBom(event: Event): void {
    if (this.isBomImportInprogress) return; // Prevent multiple API calls

    this.isBomImportInprogress = true;

    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const isValidFile = (file: File): boolean => {
      const allowedTypes = [
        'application/vnd.ms-excel',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      return allowedTypes.includes(file.type);
    };
    const resetFileInput = () => {
      input.value = '';
    };

    if (!isValidFile(file)) {
      this.toastr.showMessage('Invalid file type', 3000);
      resetFileInput();
      return;
    }

    this.informationRequestService
      .importBOM(file, this.rid, true)
      .pipe(
        finalize(() => {
          resetFileInput();
          this.isBomImportInprogress = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: ImportPFCBomResponse) => {
          if (res.is_success) {
            this.materialBreakdown = res.material_breakdown;
            this.materialsGridOverrides.rowData = this.getRawData(this.materialBreakdown.materials.lines, true);
          } else {
            this.toastr.showMessage(res.error_message, 5000);
          }
        },
        error: (err) => this.toastr.showMessage('File upload failed', 3000)
      });
  }

  getCardColor(value: any) {
    if (value) {
      const convertedValue = +value;
      if (!isNaN(convertedValue)) {
        return value > 0 ? this.successColor : this.errorContainer;
      }
    }
    return null;
  }

  updateProductFootprintData(currentCarbonEmission) {
    this.updateProductFootprintResults(currentCarbonEmission);
  }

  private init() {
    this.createResults();
    this.carbonValuePlaceholder = this.getCarbonValuePlaceholder();
    this.createProductInfoFieldTypes();
    this.createReportingFieldTypes();
    // this.createMaterialBreakDownOverrides();
    this.createGridOverrides();
    this.subscribeToCalculationMethod();
  }

  /**
   * Sets up value changes subscription for the given form.
   *
   * @param form - The form to subscribe to value changes.
   * @param formSection - The type of the form.
   */
  private setupFormValueChanges(form: FormGroup, formSection: string): void {
    form.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      takeUntilDestroyed(this.destroyRef);
      this.saveToLocalStorage(formSection, value);
    });
  }

  /**
   * Saves the form data to local storage.
   *
   * @param formSection - The type of the form.
   * @param value - The current form values.
   */
  private saveToLocalStorage(formSection: string, value: any): void {
    const key = this.generateStorageKey(formSection);
    const oldResults = this.localStorageService.getItem(key);
    const selectedForm = this.getSelectedForm(formSection);

    const saveResults = (results) => {
      const attachmentFields = selectedForm.formSchema
        .filter((e) => e.formtype === 'attachments')
        .map((e) => e.key.split('.')[0]);
      attachmentFields.forEach((element) => {
        // exlude attachments to save in localstorage

        delete results[element];
      });
      this.localStorageService.setItem(key, results);
    };

    if (!oldResults || !selectedForm.actions?.edit) {
      saveResults(value);
      return;
    }

    const updatedPayload = this.formDataService.getUpdatedFields(oldResults, value, selectedForm.formSchema);
    saveResults({ ...oldResults, ...updatedPayload });
  }

  /**
   * Retrieves the appropriate form configuration based on the form type.
   *
   * @param formSection - The type of the form.
   * @returns The selected form configuration.
   */
  private getSelectedForm(formSection: string) {
    switch (formSection) {
      case 'productInfoForm':
        return this.productInfo;
      case 'reportingForm':
        return this.reporting;
      default:
        return null;
    }
  }

  /**
   * Generates a unique storage key for the form.
   *
   * @param formSection - The type of the form.
   * @returns The generated storage key.
   */
  private generateStorageKey(formSection: string): string {
    return `PCF_${this.rid}_${formSection}`;
  }

  /**
   * Retrieves the stored results from local storage for a given form type if editing is allowed.
   *
   * @param formSection - The type of the form.
   * @returns The stored results if they exist and editing is allowed, otherwise `undefined`.
   */
  private getStoredResults(formSection: string): any {
    const selectedForm = this.getSelectedForm(formSection);

    if (selectedForm?.actions?.edit) {
      const key = this.generateStorageKey(formSection);
      return this.localStorageService.getItem(key);
    }

    return undefined;
  }

  private uploadFileAndHandleResponse(file: File) {
    const target = this.reporting.target.split('/');
    const targetRid = target[target.length - 1];

    return this.fileUploadService.fileUpload(targetRid, file).pipe(
      catchError((error) => throwError(() => new Error('File upload failed'))),
      tap((response: any) => {
        const createdObject = response[0];
        const fileId = (Object.values(createdObject) as any[])[0].file_rid;
        if (this.reportingForm.get('out_upload_files')) {
          this.reportingForm.controls['out_upload_files'].setValue({ in: fileId });
        }

        if (this.productInfoForm.get('out_upload_files')) {
          this.productInfoForm.controls['out_upload_files'].setValue({ in: fileId });
        }
      }),
      switchMap(() => this.makeApiRequests())
    );
  }

  private makeApiRequests() {
    const reportingFormValues: any = this.reportingForm.getRawValue();
    const productInfoFormValues: any = this.productInfoForm.getRawValue();
    delete productInfoFormValues['out_upload_files.in'];
    delete reportingFormValues['out_upload_files.in'];
    let requests = [];
    if (this.productInfo?.target === this.reporting?.target) {
      // if targets are same payloads need to join.
      const joinedRequest = this.informationRequestService.updateInformationRequest(this.productInfo.target, {
        ...productInfoFormValues,
        ...reportingFormValues
      });
      requests = [joinedRequest];
    } else {
      const productInfoRequest = this.informationRequestService.updateInformationRequest(
        this.productInfo.target,
        this.productInfoForm.getRawValue()
      );
      if (this.reporting?.target) {
        const reportingRequest = this.informationRequestService.updateInformationRequest(
          this.reporting.target,
          reportingFormValues
        );
        requests = [productInfoRequest, reportingRequest];
      } else requests = [productInfoRequest];
    }

    return forkJoin(requests);
  }

  private createProductInfoFieldTypes() {
    const PcfQuestionnaireOldResults = this.getStoredResults('productInfoForm');
    this.isCalculationMethod = this.productInfo.formSchema.find((e) => e.key === 'calculation_method');
    if (!this.isCalculationMethod) {
      this.isReporting = !!this.reporting; // if PCF available then true otherwise false
    }
    this.productInfoFieldTypes = this.productInfo.formSchema
      .filter((form) => !['out_attachments.in'].includes(form.key))
      .map((form: any) => {
        const formKey = form.key.split('.').pop();
        let fieldValue = PcfQuestionnaireOldResults?.[formKey] || form.defaultValue;

        let vertical = false;
        if (this.isCalculationMethod && formKey === 'calculation_method') {
          fieldValue = form.defaultValue;
          this.isReporting = fieldValue === 'PCF';
          vertical = true;
        }

        this.productInfoForm.addControl(
          form.key,
          new FormControl(fieldValue, form.mandatory ? [Validators.required] : [])
        );

        return {
          key: form.key,
          defaultValue: fieldValue,
          type: getFieldType(form.key, form.fieldtype, form.formtype),
          templateOptions: {
            label: form.question,
            info: form.question_help,
            placeholder: form.placeholder,
            disabled: ['radio'].includes(form.formtype) ? false : !this.productInfo.actions.edit,
            suffix: form.suffix,
            prefix: form.prefix,
            type: form.fieldtype,
            required: form.mandatory,
            vertical,
            options: ['radio', 'select'].includes(form.formtype) ? form.enum : [],
            link: form.link ? { ...form.link, url: this.routePipe.transform(form.link.url) } : null,
            min: form.numeric_args?.min ?? null,
            max: form.numeric_args?.max ?? null,
            onRemove: this.handleRemove.bind(this)
          }
        };
      });
    if (this.productInfo.viewData?.length) {
      const oldAttachments = this.productInfo.viewData.map((group) =>
        group.find((item) => item.key === 'out_upload_files.rid')
      );
      const attachment: any = this.productInfoFieldTypes.find((field) => field.key === 'out_upload_files.in');
      if (attachment) {
        attachment.templateOptions.attachments = oldAttachments;
      }
    }

    this.setupFormValueChanges(this.productInfoForm, 'productInfoForm');
  }

  private subscribeToCalculationMethod() {
    (this.productInfoForm.controls as any)?.calculation_method?.valueChanges
      .pipe(
        startWith((this.productInfoForm.controls as any)?.calculation_method?.value as string),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((calculationMethod) => {
        if (calculationMethod === 'PCF') {
          this.isReporting = true;
          this.updateProductFootprintResults(
            (this.reportingForm?.controls as any)?.reporting_carbon_footprint?.value ?? 0
          );
          this.calculateCarbonFootprint = false;
        } else {
          this.isReporting = false;
          this.calculateCarbonFootprint = true;
        }
        this.setResultsData();
      });
  }

  private createReportingFieldTypes() {
    if (!this.reporting) return;
    const PcfQuestionnaireOldResults = this.getStoredResults('reportingForm');
    this.reportingFieldTypes = this.reporting.formSchema
      .filter((form) => !['out_attachments.in'].includes(form.key))
      .map((form: any) => {
        const key = form.key.split('.')[0]; // with . it nests formControls
        const fieldValue = PcfQuestionnaireOldResults?.[key] || form.defaultValue;
        this.reportingForm.addControl(key, new FormControl(fieldValue));
        const vertical = form.key.includes('out_upload_files');
        return {
          key,
          defaultValue: fieldValue,
          type: getFieldType(form.key, form.fieldtype, form.formtype),
          templateOptions: {
            label: form.question,
            info: form.question_help,
            placeholder: form.placeholder,
            disabled: !this.reporting.actions.edit,
            suffix: form.suffix,
            prefix: form.prefix,
            blur: (field: FormlyFieldConfig, event: FocusEvent) => this.setCarbonCalculation(field),
            options: ['radio', 'select'].includes(form.formtype) ? form.enum : [],
            type: form.fieldtype,
            required: form.mandatory,
            vertical,
            onRemove: this.handleRemove.bind(this)
          }
        };
      });
    // for attachments
    if (this.reporting.viewData?.length) {
      if (this.reporting.viewData?.length) {
        const oldAttachments = this.reporting.viewData.map((group) =>
          group.find((item) => item.key === 'out_upload_files.rid')
        );
        const attachment: any = this.reportingFieldTypes.find((field) => field.key === 'out_upload_files');
        if (attachment) {
          attachment.templateOptions.attachments = oldAttachments;
        }
      }
    }
    const reportingCardingFootprintField: any = this.reportingFieldTypes.find(
      (item) => item.key === 'reporting_carbon_footprint'
    );
    if (reportingCardingFootprintField) {
      this.updateProductFootprintResults(reportingCardingFootprintField.defaultValue);
    }

    this.setupFormValueChanges(this.reportingForm, 'reportingForm');
  }

  private createGridOverrides() {
    if (!this.materialBreakdown) return;
    this.materialsGridOverrides = {
      columnDefs: this.getColumnDefinition(this.materialBreakdown.materials.formSchema, true),
      rowData: this.getRawData(this.materialBreakdown.materials.lines, true),
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: false,
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true,
        suppressMovable: true
      },
      groupDefaultExpanded: -1,
      rowSelection: RowSelection.MULTIPLE,
      treeGrid: false,
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show in table'
      }
    };
    this.processesGridOverrides = {
      columnDefs: this.getColumnDefinition(this.materialBreakdown.processes.formSchema, false),
      rowData: this.getRawData(this.materialBreakdown.processes.lines, false),
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: false,
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true,
        suppressMovable: true
      },
      groupDefaultExpanded: -1,
      rowSelection: RowSelection.MULTIPLE,
      treeGrid: false,
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show in table'
      }
    };
    this.cdr.detectChanges();
  }

  // TODO add types
  private getRawData(lines: any[], isMaterials: boolean) {
    const actions = isMaterials ? this.materialBreakdown.materials.actions : this.materialBreakdown.processes.actions;
    const formSchema = isMaterials
      ? this.materialBreakdown.materials.formSchema
      : this.materialBreakdown.processes.formSchema;
    const rowData = [];
    lines.forEach((rows) => {
      const rowObj: Record<string, any> = {};
      rowObj.row = rows;
      rowObj.actions = {
        view: actions.view,
        edit: actions.edit,
        delete: actions.delete
      };
      rowObj.formSchema = formSchema;
      rowObj.editedCell = [];

      rows.forEach((rowItem) => {
        rowItem.key = rowItem.key.replace('$', '.');
        let key = rowItem.key.split('.');
        key = key.pop();
        if (rowItem.link) {
          rowObj[`Link_${rowItem.label}`] = rowItem.link;
        }
        if (typeof rowItem.value === 'number') {
          rowObj[key] = rowItem.value ?? rowItem.display_value;
        } else {
          rowObj[key] = rowItem.display_value;
        }
      });
      rowData.push(rowObj);
    });
    return rowData;
  }

  private handleRemove(rid: string) {
    this.informationRequestService.deleteAttachment(rid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private createResults() {
    this.productFootprintResults = this.getResultsFromFormSchema(this.results);
    this.setResultsData();
  }

  private setResultsData() {
    const cloned = _.cloneDeep(this.resultSection);

    // === Previous Reported Footprint ===
    cloned[0].value.headerBottom.data =
      this.productFootprintResults?.prev_reported_footprint || (this.carbonValuePlaceholder as any);

    cloned[0].value.list[0].value = this.getFormattedDate(
      this.productFootprintResults?.prev_reported_footprint_at
    ) as any;

    cloned[0].value.headerBottom.unit = this.productFootPrintUnits.prev_reported_footprint as any;

    // === Current Calculated Footprint ===
    cloned[1].value.headerBottom.data =
      this.productFootprintResults?.curr_calculated_footprint || (this.carbonValuePlaceholder as any);

    cloned[1].value.list[0].value = this.getFormattedDate(
      this.productFootprintResults?.curr_calculated_footprint_at
    ) as any;
    cloned[1].value.headerBottom.icon = this.calculateCarbonFootprint ? MdsIcon.WARNING : null;

    cloned[1].value.headerBottom.unit = this.productFootPrintUnits.curr_calculated_footprint as any;

    // === Carbon Footprint Reduction ===
    cloned[2].value.headerBottom.data =
      this.productFootprintResults?.carbon_footprint_reduction || (this.carbonValuePlaceholder as any);
    cloned[2].value.headerBottom.icon = this.calculateCarbonFootprint ? MdsIcon.WARNING : null;

    cloned[2].value.list[0].value = this.getFormattedDate(
      this.productFootprintResults?.carbon_footprint_reduction_at
    ) as any;

    cloned[2].value.headerBottom.unit = this.productFootPrintUnits.carbon_footprint_reduction as any;

    this.resultSection = cloned;
    this.cdr.detectChanges();
  }

  private getFormattedDate(date: any) {
    const datePipe = new DatePipe('en-US');
    const formatted = date ? datePipe.transform(parseAsUTC(date), "dd.MM.yyyy, 'at' HH:mm") : '--';
    return formatted;
  }

  private getResultsFromFormSchema(productResult: any) {
    const results = {
      title: productResult.name,
      prev_reported_footprint: null,
      prev_reported_footprint_at: null,
      curr_calculated_footprint: null,
      curr_calculated_footprint_at: null,
      carbon_footprint_reduction: null,
      carbon_footprint_reduction_at: null
    };

    productResult.formSchema.forEach((schema) => {
      this.productFootPrintUnits[schema.key] = schema.suffix;
      results[schema.key] = schema.defaultValue;
    });

    if (results.curr_calculated_footprint) {
      results.carbon_footprint_reduction =
        (Number(results.prev_reported_footprint) ?? 0) - (Number(results.curr_calculated_footprint) ?? 0);
      results.carbon_footprint_reduction_at = results?.curr_calculated_footprint_at ?? '';
    }
    return results;
  }

  private setCarbonCalculation(field: FormlyFieldConfig) {
    if (field.key !== 'reporting_carbon_footprint') {
      return;
    }
    const currentCarbonEmission = Number(this.reportingForm.get(field.key).value) ?? 0;
    this.updateProductFootprintData(currentCarbonEmission);
  }

  private updateProductFootprintResults(currentCarbonEmission: any) {
    if (currentCarbonEmission === null || currentCarbonEmission === undefined || currentCarbonEmission === '') {
      return;
    }
    const currentDate = new Date();
    const reportedCarbonFootprint = this.productFootprintResults?.prev_reported_footprint;
    const reduction = currentCarbonEmission ? (Number(reportedCarbonFootprint) ?? 0) - currentCarbonEmission : 0;
    this.productFootprintResults.curr_calculated_footprint = currentCarbonEmission;
    this.productFootprintResults.curr_calculated_footprint_at = currentDate;
    this.productFootprintResults.carbon_footprint_reduction = reduction;
    this.productFootprintResults.carbon_footprint_reduction_at = currentDate;
    this.setResultsData();
  }

  private showError(err: HttpErrorResponse) {
    this.toastr.showMessage(err?.error?.message ?? 'Something went wrong', 1000);
  }

  onSwitchView(selectedTab: { label: string; value: CampaignTabsEnum }): void {
    this.selectedTab = selectedTab.value;
  }
}
